import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { toLocalISODate } from './attendanceMetricUtils';

export type AttendanceWhenChip = 'today' | 'month' | 'year';
export type AttendanceWhoScope = 'school' | 'class';

export type AttendanceClassOption = {
  id: string;
  name?: string;
  gradeLevel?: string;
  sectionName?: string;
};

const fieldClass =
  'rounded-lg border border-slate-300 bg-white px-3 py-2 text-base sm:text-sm min-h-[40px] text-slate-900 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30';

const motionEase = 'duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none';

export function formatAttendanceClassLabel(item: AttendanceClassOption) {
  const grade = item.gradeLevel?.trim();
  const section = item.sectionName?.trim();
  if (grade && section) {
    return `${grade} - ${section}`;
  }
  return item.name?.trim() || 'Class';
}

export function deriveAttendanceWhenChip(args: {
  timeFilterMode: 'period' | 'day';
  periodSubType: 'monthly' | 'yearly' | 'custom';
}): AttendanceWhenChip {
  if (args.timeFilterMode === 'day') return 'today';
  if (args.periodSubType === 'yearly') return 'year';
  return 'month';
}

export function currentMonthToTodayRange(now = new Date()) {
  return {
    start: toLocalISODate(new Date(now.getFullYear(), now.getMonth(), 1)),
    end: toLocalISODate(now),
  };
}

function SlidingChipGroup<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [pill, setPill] = useState({ left: 0, width: 0, ready: false });

  const updatePill = useCallback(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const active = wrap.querySelector<HTMLElement>(`[data-chip="${value}"]`);
    if (!active) return;
    setPill({ left: active.offsetLeft, width: active.offsetWidth, ready: true });
  }, [value]);

  useLayoutEffect(() => {
    updatePill();
  }, [updatePill]);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return undefined;
    const observer = new ResizeObserver(() => updatePill());
    observer.observe(wrap);
    window.addEventListener('resize', updatePill);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updatePill);
    };
  }, [updatePill]);

  return (
    <div
      ref={wrapRef}
      className="relative isolate inline-flex max-w-full flex-nowrap gap-1 overflow-x-auto rounded-lg bg-slate-100 p-1.5"
    >
      <span
        aria-hidden
        className={clsx(
          'pointer-events-none absolute top-1 bottom-1 rounded-md bg-white shadow-sm',
          pill.ready ? clsx('opacity-100 transition-[left,width]', motionEase) : 'opacity-0'
        )}
        style={{ left: pill.left, width: pill.width }}
      />
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          data-chip={option.value}
          className={clsx(
            'relative z-10 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-semibold min-h-[38px] flex items-center justify-center transition-colors duration-200 motion-reduce:transition-none',
            value === option.value ? 'text-brand' : 'text-slate-500 hover:text-slate-800'
          )}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function ExpandFade({ show, children }: { show: boolean; children: ReactNode }) {
  return (
    <div
      aria-hidden={!show}
      className={clsx(
        'grid min-w-0 transition-[grid-template-rows,opacity,margin]',
        motionEase,
        show ? 'mt-0 grid-rows-[1fr] opacity-100' : 'pointer-events-none mt-0 grid-rows-[0fr] opacity-0'
      )}
    >
      <div className="min-h-0 overflow-hidden">
        <div className={clsx('transition-transform', motionEase, show ? 'translate-y-0' : '-translate-y-1')}>
          {children}
        </div>
      </div>
    </div>
  );
}

export function AttendanceAnswerFade({ motionKey, children }: { motionKey: string; children: ReactNode }) {
  return (
    <div key={motionKey} className="attendance-answer-fade">
      {children}
    </div>
  );
}

export function AttendanceWhoWhenBar({
  scope,
  onScopeChange,
  classId,
  onClassChange,
  classes,
  whenChip,
  onWhenChip,
  selectedMonth,
  selectedYear,
  onMonthChange,
  onYearChange,
  availableYears,
  dateRange,
  onDateRangeChange,
}: {
  scope: AttendanceWhoScope;
  onScopeChange: (scope: AttendanceWhoScope) => void;
  classId: string;
  onClassChange: (classId: string) => void;
  classes: AttendanceClassOption[];
  whenChip: AttendanceWhenChip;
  onWhenChip: (chip: AttendanceWhenChip) => void;
  selectedMonth: number;
  selectedYear: number;
  onMonthChange: (month: number) => void;
  onYearChange: (year: number) => void;
  availableYears: { min: number; max: number };
  dateRange?: { start: string; end: string };
  onDateRangeChange?: (range: { start: string; end: string }) => void;
}) {
  const years = Array.from(
    { length: Math.max(1, availableYears.max - availableYears.min + 1) },
    (_, index) => availableYears.max - index
  );

  return (
    <CardLike>
      <div className="flex flex-col gap-6 lg:grid lg:grid-cols-2 lg:gap-8">
        <div className="min-w-0 space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Who</p>
          <div className="flex flex-col items-start gap-3">
            <SlidingChipGroup
              value={scope}
              onChange={onScopeChange}
              options={[
                { value: 'school', label: 'Whole school' },
                { value: 'class', label: 'One class' },
              ]}
            />
            <ExpandFade show={scope === 'class'}>
              <select
                aria-label="Class"
                value={classId}
                onChange={(event) => onClassChange(event.target.value)}
                tabIndex={scope === 'class' ? 0 : -1}
                className={clsx(fieldClass, 'w-full min-w-[220px]')}
              >
                <option value="">Choose a class</option>
                {classes.map((item) => (
                  <option key={item.id} value={item.id}>
                    {formatAttendanceClassLabel(item)}
                  </option>
                ))}
              </select>
            </ExpandFade>
          </div>
        </div>

        <div className="min-w-0 space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">When</p>
          <div className="flex flex-col items-start gap-3">
            <SlidingChipGroup
              value={whenChip}
              onChange={onWhenChip}
              options={[
                { value: 'today', label: 'Today' },
                { value: 'month', label: 'This month' },
                { value: 'year', label: 'This year' },
              ]}
            />
            <div className="w-full min-w-0">
              <ExpandFade show={whenChip === 'month'}>
                <div className="flex flex-wrap gap-2">
                  <select
                    aria-label="Month"
                    value={selectedMonth}
                    onChange={(event) => onMonthChange(Number(event.target.value))}
                    tabIndex={whenChip === 'month' ? 0 : -1}
                    className={fieldClass}
                  >
                    {Array.from({ length: 12 }, (_, index) => index + 1).map((month) => (
                      <option key={month} value={month}>
                        {new Date(2000, month - 1, 1).toLocaleString('default', { month: 'long' })}
                      </option>
                    ))}
                  </select>
                  <select
                    aria-label="Year"
                    value={selectedYear}
                    onChange={(event) => onYearChange(Number(event.target.value))}
                    tabIndex={whenChip === 'month' ? 0 : -1}
                    className={fieldClass}
                  >
                    {years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </ExpandFade>
              <ExpandFade show={whenChip === 'year'}>
                <select
                  aria-label="Year"
                  value={selectedYear}
                  onChange={(event) => onYearChange(Number(event.target.value))}
                  tabIndex={whenChip === 'year' ? 0 : -1}
                  className={fieldClass}
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </ExpandFade>
            </div>
          </div>
        </div>
      </div>
    </CardLike>
  );
}

function CardLike({ children }: { children: ReactNode }) {
  return (
    <div data-testid="attendance-who-when" className="rounded-xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
      {children}
    </div>
  );
}
